import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star } from 'lucide-react';

const FAV_KEY = 'accounting_reports_favorites_v1';

/**
 * Reads the user's pinned reports from localStorage (same key used on the
 * Accounting Reports Hub) and renders a compact list in the sidebar so they
 * can be reached from ANY page with one click.
 *
 * Hidden entirely when no favorites — no empty state that wastes sidebar space.
 */
export default function SidebarFavorites({ hidden = false }) {
  const [favorites, setFavorites] = useState([]);
  const location = useLocation();

  // Poll localStorage changes. Use a `storage` event listener for cross-tab
  // sync, and a custom event so the Reports Hub can push updates in-tab too.
  useEffect(() => {
    const load = () => {
      try { setFavorites(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); }
      catch { setFavorites([]); }
    };
    load();
    window.addEventListener('storage', load);
    window.addEventListener('sidebar-favorites-updated', load);
    return () => {
      window.removeEventListener('storage', load);
      window.removeEventListener('sidebar-favorites-updated', load);
    };
  }, []);

  if (hidden) return null;
  if (!favorites.length) return null;

  // Human-readable label from the route path — "/profit-loss" → "Profit Loss"
  const label = (path) => {
    const slug = (path || '').replace(/^\//, '').replace(/-/g, ' ');
    return slug.replace(/\b\w/g, c => c.toUpperCase()) || path;
  };

  return (
    <div className="px-2.5 mb-3" data-testid="sidebar-favorites">
      <div className="px-2 pt-2 pb-1 flex items-center gap-1.5">
        <Star size={11} className="text-amber-500" fill="currentColor" />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Favorites</span>
      </div>
      <div className="space-y-0.5">
        {favorites.map((path) => {
          const isActive = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              className={`flex items-center gap-2 px-3 py-[6px] rounded-md text-[12px] transition-all ${isActive ? 'bg-amber-50 text-amber-700 font-semibold dark:bg-amber-900/20' : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'}`}
              data-testid={`sidebar-fav-${path.slice(1)}`}
            >
              <Star size={10} className={isActive ? 'text-amber-500' : 'opacity-40'} fill={isActive ? 'currentColor' : 'none'} />
              <span className="truncate">{label(path)}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
